import { Graph3D } from '@packages/graph-3d';
import React, { useEffect, useMemo } from 'react';
import { useConfig } from '../../../../hooks/useConfig';
import type { OrganizationDetail } from '../../../../api';
import type { OrganizationRuntimeState } from './types';
import { resolvePersonAvatarSources } from './avatarDemo';
import {
  buildOrganizationGraph3DData,
  type OrganizationGraph3DNodeData,
} from './adapters/buildOrganizationGraph3DData';
import { buildOrganizationRuntimeView } from './adapters/buildOrganizationRuntimeView';
import {
  compileOrganizationGraph,
  type OrganizationGraphCompileResult,
} from './utils/organizationGraph';

interface OrganizationDiagram3DProps {
  detail: OrganizationDetail | null;
  orgStates: Map<string, OrganizationRuntimeState>;
  suppressMotion?: boolean;
  onCompiled?: (graph: OrganizationGraphCompileResult) => void;
  mode?: 'runtime' | 'management';
}

const OrganizationDiagram3D: React.FC<OrganizationDiagram3DProps> = ({
  detail,
  orgStates,
  onCompiled,
}) => {
  const { globalConfig } = useConfig();
  const enablePersonAvatarDemo = globalConfig.enablePersonAvatarDemo !== false;

  const compiled = useMemo(() => {
    if (!detail) {
      return null;
    }
    return compileOrganizationGraph(detail);
  }, [detail]);

  useEffect(() => {
    if (!compiled || !onCompiled) {
      return;
    }
    onCompiled(compiled);
  }, [compiled, onCompiled]);

  const data = useMemo(() => {
    if (!compiled) {
      return null;
    }
    const base = buildOrganizationGraph3DData(compiled);
    return {
      ...base,
      nodes: base.nodes.map(node => {
        if (node.type !== 'person') {
          return node;
        }
        const currentData = node.data as OrganizationGraph3DNodeData;
        const { avatarUrl, avatarFallbackUrls } = resolvePersonAvatarSources(
          node.label,
          currentData.avatarUrl,
          enablePersonAvatarDemo
        );
        return {
          ...node,
          data: {
            ...currentData,
            avatarUrl,
            avatarFallbackUrls,
          },
        };
      }),
    };
  }, [compiled, enablePersonAvatarDemo]);

  const runtime = useMemo(() => {
    if (!compiled) {
      return undefined;
    }
    return buildOrganizationRuntimeView(compiled, orgStates);
  }, [compiled, orgStates]);

  if (!data) {
    return <div className="h-full w-full bg-white" />;
  }

  return (
    <div className="h-full w-full bg-white">
      <Graph3D
        data={data}
        runtime={runtime}
        layout={{ type: 'preset', center: true, zGap: 18 }}
        interactions={{
          enableAutoFit: true,
          enableRuntimeAutoFocus: false,
          enableHoverDetailPanel: false,
        }}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default OrganizationDiagram3D;
